import type { MenuItem } from "./menu.js";
import { messageOf, type ScreenHost } from "./ui.js";

/** What Order by category says (g81). Exported so the tests pin the words. */
export const ORDER_BY = {
  label: "Order by category",
  on: "List ordered by category.",
  off: "List back in its normal order.",
  unsaved: "Could not save the list's order on this device. The list is as it was.",
  draftTitle: "Save your edit and order the list?",
  draftMessage: "Ordering by category fills empty categories from the saved text, so your edit is saved first.",
  draftKeep: "Keep editing",
  draftAct: "Save and order",
} as const;

/** What the screen says once the switch is on: that it is, and how many categories it filled when any were. */
export function orderedMessage(filled: number): string {
  if (filled === 0) return ORDER_BY.on;
  return `${ORDER_BY.on} ${filled} ${filled === 1 ? "category" : "categories"} filled.`;
}

/**
 * Order by category in a list's header menu (g81): one checkable entry, on
 * every kind of list, that shows a tick while it is on. Switched on, the
 * list is shown by category and, inside one, in its normal order; switched
 * off, the normal order is shown again, untouched. It replaces the one-time
 * Auto-order, which rewrote the order and was offered on Shopping lists only.
 *
 * Switching it on fills each blank category on a Shopping list that the
 * classifier places. Those are ordinary categories and stay when it is
 * switched off, so there is no Undo.
 *
 * Beside `listActions` rather than in it, because the Lists overview shares
 * those entries and has no rows to order.
 */
export function orderByAction(options: {
  shell: ScreenHost;
  listId: string;
  /** Shows routine feedback where the screen shows it (g102): briefly, and in the flow. */
  say(message: string): void;
  /**
   * Settles the screen's drafts and gestures before a switch that fills
   * categories, then calls `proceed`, or does not when the person keeps
   * editing or a draft is refused.
   */
  prepare(proceed: () => void): void;
  /** Ends a drag in flight: the gaps it was aimed at are about to move. */
  cancelDrag(): void;
  /** Runs a store command with the screen's rows repainted once. */
  apply<R>(command: () => Promise<R>): Promise<R>;
  problem(message: string): void;
  refreshMenu(): void;
}) {
  const { shell, listId } = options;
  const { store } = shell;
  let busy = false;
  let disposed = false;

  const on = () => store.list(listId)?.orderBy === "category";
  const say = (message: string) => {
    options.say(message);
    shell.announce(message);
  };

  function run(): void {
    if (busy || disposed) return;
    const list = store.list(listId);
    if (!list) return;
    // Only a switch that fills reads the saved text, so only it asks about a
    // draft. Any other just redraws the rows, and an open editor moves with
    // its row and keeps what was typed.
    if (!on() && list.kind === "shopping") options.prepare(() => void change());
    else { options.cancelDrag(); void change(); }
  }

  async function change(): Promise<void> {
    if (busy || disposed) return;
    busy = true;
    options.refreshMenu();
    try {
      // Read now, after any draft question: the owner may have switched it elsewhere meanwhile.
      const next = on() ? null : "category";
      const result = await options.apply(() => store.setListOrder(listId, next));
      if (disposed) return;
      if (result.status === "saved") say(next === null ? ORDER_BY.off : orderedMessage(result.filled));
      else if (result.status === "unsaved") { options.problem(ORDER_BY.unsaved); shell.announce(ORDER_BY.unsaved); }
    } catch (error) {
      if (!disposed) options.problem(messageOf(error));
    } finally {
      busy = false;
      if (!disposed) options.refreshMenu();
    }
  }

  return {
    /** The entry, on every kind; read each time, so a switch made elsewhere shows its tick. */
    entries: (): MenuItem[] => [{ label: ORDER_BY.label, icon: "sort", checked: on(), disabled: busy, onSelect: run }],
    dispose(): void { disposed = true; },
  };
}
